import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import {
  brandColours,
  checkboxStyles,
  fontSize,
  fontWeights,
  minBreakpointQuery,
  standardColours,
  visuallyHidden,
} from '../styles';
import { Button, Link } from './ui';
import Modal from './Modal';

const StyledCookieModal = styled(Modal)`
  border-radius: 20px;
  padding: 10px;
  max-height: 90%;
  overflow-y: scroll;
  color: ${brandColours.primary};

  ${minBreakpointQuery.smedium`
    max-width: 580px;
    padding: 35px 45px;
  `}
`;

const StyledText = styled.p`
  padding: 0 18px;
  line-height: 1.5;
`;

const StyledLink = styled(Link)`
  color: ${brandColours.tertiary};
`;

const StyledForm = styled.form`
  padding: 18px;
  display: grid;
  gap: 10px;
`;

const StyledItem = styled.div`
  border: 1px solid ${brandColours.primary};
  border-radius: 10px;
  padding: 16px;
`;

const StyledItemTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledCheckboxWrapper = styled.label``;

const StyledCheckboxText = styled.span`
  ${checkboxStyles()};
  color: ${brandColours.primary};
  ${fontSize(16)};
  font-weight: ${fontWeights.bold};
  padding-left: 35px;

  &:before,
  &:after {
    cursor: pointer;
  }

  &:before {
    border: 1px solid ${brandColours.primary};
    border-radius: 5px;
    width: 25px;
    height: 25px;
  }

  &:after {
    border: 1px solid ${standardColours.white};
    border-top: 0;
    border-right: 0;
    left: 8px;
    height: 5px;
    width: 10px;
  }
`;

const StyledCheckbox = styled.input`
  ${visuallyHidden()};

  &:checked + ${StyledCheckboxText} {
    &:before {
      background-color: ${brandColours.primary};
    }

    &:after {
      opacity: 1;
    }
  }
`;

const StyledView = styled.button`
  position: relative;
  width: 16px;
  height: 16px;

  span {
    ${visuallyHidden()}
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 2px;
    width: 12px;
    height: 12px;
    border: 2px solid ${brandColours.primary};
    border-top: none;
    border-left: none;
    transform: rotate(45deg);
    cursor: pointer;
  }
`;

const StyledItemText = styled.p`
  margin-top: 20px;
  line-height: 1.5;
  display: ${({ display }) => (display ? 'block' : 'none')};
`;

const StyledButtons = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;

  ${minBreakpointQuery.smedium`
    display: flex;
  `}
`;

const StyledButton = styled(Button)`
  border-radius: 25px;

  ${minBreakpointQuery.smedium`
    padding-left: 40px;
    padding-right: 40px;
  `}

  ${({ isSave }) => {
    if (isSave) {
      return css`
        border: 1px solid ${brandColours.primary};
        background-color: transparent;
        color: ${brandColours.primary};
      `;
    }
  }}
`;

const CookieModal = ({
  formRef,
  cookies,
  handleSubmit,
  isCookieModalActive,
}) => {
  const [activeItem, setActiveItem] = useState(null);

  return (
    <StyledCookieModal
      heading="Manage your cookie preferences"
      hideClose={true}
      isOpen={isCookieModalActive}
      alignItems="start"
    >
      <StyledText>
        We use cookies to make your experience better. By using spaseekers.com,
        you accept our{' '}
        <StyledLink to="/privacy-policy/">cookie notice</StyledLink> terms.
      </StyledText>
      <StyledForm
        id="cookie-preferences"
        method="POST"
        ref={formRef}
        onSubmit={e => handleSubmit(e)}
      >
        {cookies.map(({ name, heading, text, required }, id) => (
          <StyledItem key={id}>
            <StyledItemTop>
              <StyledCheckboxWrapper>
                <StyledCheckbox
                  type="checkbox"
                  name={name}
                  value="granted"
                  disabled={required}
                  defaultChecked
                />
                <StyledCheckboxText>{heading}</StyledCheckboxText>
              </StyledCheckboxWrapper>
              <StyledView
                onClick={e => {
                  e.preventDefault();
                  setActiveItem(activeItem === id ? null : id);
                }}
              >
                <span>Show/hide text</span>
              </StyledView>
            </StyledItemTop>
            <StyledItemText display={activeItem === id}>{text}</StyledItemText>
          </StyledItem>
        ))}
        <StyledButtons>
          <StyledButton type="submit" isSave={true}>
            Save
          </StyledButton>
          <StyledButton
            onClick={e => {
              e.preventDefault();
              handleSubmit(e, true);
            }}
          >
            Accept all
          </StyledButton>
        </StyledButtons>
      </StyledForm>
    </StyledCookieModal>
  );
};

export default CookieModal;
